// frontend/src/pages/Profile/Profile.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './Profile.css';

const GRADE_8_9_TOPICS = [
  'Whole Numbers', 'Integers', 'Common Fractions', 'Decimal Fractions',
  'Exponents', 'Ratio and Rate', 'Percentages', 'Financial Mathematics',
  'Numeric Patterns', 'Geometric Patterns', 'Algebraic Expressions',
  'Algebraic Equations', 'Graphs', 'Geometry of 2D Shapes',
  'Geometry of 3D Objects', 'Geometric Constructions', 'Transformation Geometry',
  'Area and Perimeter', 'Surface Area and Volume', 'Theorem of Pythagoras',
  'Collect and Organize Data', 'Represent Data', 'Interpret Data', 'Probability'
];

const GRADE_10_12_TOPICS = [
  'Algebra', 'Functions', 'Finance', 'Trigonometry',
  'Euclidean Geometry', 'Analytical Geometry', 'Statistics',
  'Probability', 'Calculus', 'Patterns and Sequences', 'Number Patterns'
];

const getTopicsForGrade = (grade) => {
  const g = parseInt(grade);
  if (g >= 8 && g <= 9) return GRADE_8_9_TOPICS;
  if (g >= 10 && g <= 12) return GRADE_10_12_TOPICS;
  return GRADE_10_12_TOPICS;
};

const Profile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [createdContent, setCreatedContent] = useState([]);
  const [savedContent, setSavedContent] = useState([]);
  const [createdLoading, setCreatedLoading] = useState(false);
  const [savedLoading, setSavedLoading] = useState(false);
  const [followOptimistic, setFollowOptimistic] = useState(null);
  const [followActionLoading, setFollowActionLoading] = useState(false);

  const availableTopics = getTopicsForGrade(editForm.grade || profile?.profile?.grade || 10);
  const currentlyFollowing = followOptimistic !== null ? followOptimistic : profile?.isFollowing;

  useEffect(() => {
    fetchProfile();
    setFollowOptimistic(null);
    setActiveTab('overview');
  }, [username]);

  useEffect(() => {
    if (activeTab === 'posts') fetchUserPosts();
    else if (activeTab === 'stats') fetchUserStats();
    else if (activeTab === 'achievements') fetchAchievements();
    else if (activeTab === 'created') fetchCreatedContent();
    else if (activeTab === 'saved') fetchSavedContent();
  }, [activeTab, username]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`/api/profile/${username}`, { headers });
      setProfile(response.data);
      setEditForm({
        name: response.data.profile?.name || '',
        bio: response.data.profile?.bio || '',
        grade: response.data.profile?.grade || 10,
        preferredTopics: response.data.profile?.preferredTopics || []
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      if (error.response?.status === 404) navigate('/404');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPosts = async () => {
    try {
      const response = await axios.get(`/api/profile/${username}/posts`);
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await axios.get(`/api/profile/${username}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAchievements = async () => {
    try {
      const response = await axios.get(`/api/profile/${username}/achievements`);
      setAchievements(response.data || []);
    } catch (error) {
      console.error('Error fetching achievements:', error);
      setAchievements([]);
    }
  };

  const fetchCreatedContent = async () => {
    setCreatedLoading(true);
    try {
      const response = await axios.get(`/api/profile/${username}/created`);
      setCreatedContent(response.data.items || response.data || []);
    } catch (error) {
      console.error('Error fetching created content:', error);
      setCreatedContent([]);
    } finally {
      setCreatedLoading(false);
    }
  };

  const fetchSavedContent = async () => {
    setSavedLoading(true);
    try {
      const response = await api.get('/api/profile/saved');
      setSavedContent(response.data.savedContent || response.data || []);
    } catch (error) {
      console.error('Error fetching saved content:', error);
      setSavedContent([]);
    } finally {
      setSavedLoading(false);
    }
  };

  const handleGradeChange = (newGrade) => {
    const grade = parseInt(newGrade);
    const newAvailableTopics = getTopicsForGrade(grade);
    const validTopics = (editForm.preferredTopics || []).filter(t =>
      newAvailableTopics.includes(t)
    );
    setEditForm({ ...editForm, grade: grade, preferredTopics: validTopics });
  };

  const handleUpdateProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        'profile.name': editForm.name,
        'profile.bio': editForm.bio,
        'profile.grade': editForm.grade,
        'profile.preferredTopics': editForm.preferredTopics
      };
      await axios.put('/api/profile/update', updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (user) {
        updateUser({
          ...user,
          profile: {
            ...user.profile,
            name: editForm.name,
            bio: editForm.bio,
            grade: editForm.grade,
            preferredTopics: editForm.preferredTopics
          }
        });
      }
      setIsEditing(false);
      fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile');
    }
  };

  const handleFollow = async () => {
    setFollowOptimistic(true);
    setFollowActionLoading(true);
    try {
      await api.post(`/api/profile/follow/${profile._id}`);
      fetchProfile();
    } catch (error) {
      console.error('Error following user:', error);
      setFollowOptimistic(false);
    } finally {
      setFollowActionLoading(false);
    }
  };

  const handleUnfollow = async () => {
    setFollowOptimistic(false);
    setFollowActionLoading(true);
    try {
      await api.post(`/api/profile/unfollow/${profile._id}`);
      fetchProfile();
    } catch (error) {
      console.error('Error unfollowing user:', error);
      setFollowOptimistic(true);
    } finally {
      setFollowActionLoading(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/profile/avatar', formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      fetchProfile();
    } catch (error) {
      console.error('Error updating avatar:', error);
      alert('Error updating avatar');
    }
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-container">
        <h1>Profile not found</h1>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="profile-avatar-section">
          <div className="profile-avatar">
            {profile.profile?.avatar ? (
              <img src={`${profile.profile.avatar}`} alt={username} />
            ) : (
              <div className="avatar-placeholder">
                {profile.profile?.name?.[0] || username[0]?.toUpperCase()}
              </div>
            )}
            {profile.isOwnProfile && (
              <>
                <input type="file" id="avatar-upload" accept="image/*"
                  onChange={handleAvatarChange} style={{ display: 'none' }} />
                <label htmlFor="avatar-upload" className="avatar-upload-btn">
                  {'\uD83D\uDCF7'}
                </label>
              </>
            )}
          </div>
        </div>

        <div className="profile-info">
          {isEditing ? (
            <div className="edit-form">
              <input type="text" value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Full Name" className="edit-input" />
              <textarea value={editForm.bio}
                onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                placeholder="Bio" className="edit-textarea" maxLength="160" />
              <select value={editForm.grade}
                onChange={(e) => handleGradeChange(e.target.value)} className="edit-select">
                <option value="8">Grade 8</option>
                <option value="9">Grade 9</option>
                <option value="10">Grade 10</option>
                <option value="11">Grade 11</option>
                <option value="12">Grade 12</option>
              </select>
              <div className="topics-selection">
                <label>Preferred Topics:</label>
                <div className="topics-grid">
                  {availableTopics.map(topic => (
                    <label key={topic} className="topic-checkbox">
                      <input type="checkbox"
                        checked={editForm.preferredTopics?.includes(topic)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditForm({ ...editForm,
                              preferredTopics: [...(editForm.preferredTopics || []), topic] });
                          } else {
                            setEditForm({ ...editForm,
                              preferredTopics: editForm.preferredTopics?.filter(t => t !== topic) || [] });
                          }
                        }}
                      />
                      <span>{topic}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="edit-actions">
                <button onClick={handleUpdateProfile} className="save-btn">Save</button>
                <button onClick={() => setIsEditing(false)} className="cancel-btn">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <h1>{profile.profile?.name || username}</h1>
              <p className="profile-username">@{username}</p>
              {profile.profile?.bio && <p className="profile-bio">{profile.profile.bio}</p>}
              <div className="profile-details">
                <span className="grade-badge">Grade {profile.profile?.grade || 10}</span>
                {profile.profile?.preferredTopics?.map(topic => (
                  <span key={topic} className="topic-badge">{topic}</span>
                ))}
              </div>
            </>
          )}

          <div className="profile-actions">
            {profile.isOwnProfile ? (
              <button onClick={() => setIsEditing(!isEditing)} className="edit-profile-btn">
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </button>
            ) : (
              user && (
                currentlyFollowing ? (
                  <button onClick={handleUnfollow}
                    className="unfollow-btn" disabled={followActionLoading}>
                    {followActionLoading ? '...' : 'Following'}
                  </button>
                ) : (
                  <button onClick={handleFollow}
                    className="follow-btn" disabled={followActionLoading}>
                    {followActionLoading ? '...' : 'Follow'}
                  </button>
                )
              )
            )}
          </div>
        </div>
      </div>

      <div className="profile-stats">
        <div className="stat-item">
          <span className="stat-value">{profile.stats?.postsCount || 0}</span>
          <span className="stat-label">Posts</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{profile.stats?.followersCount || 0}</span>
          <span className="stat-label">Followers</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{profile.stats?.followingCount || 0}</span>
          <span className="stat-label">Following</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{profile.stats?.questionsSolved || 0}</span>
          <span className="stat-label">Questions</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{profile.stats?.createdCount || 0}</span>
          <span className="stat-label">Created</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">
            {profile.stats?.questionsSolved > 0
              ? Math.round((profile.stats.correctAnswers / profile.stats.questionsSolved) * 100)
              : 0}%
          </span>
          <span className="stat-label">Accuracy</span>
        </div>
      </div>

      <div className="profile-tabs">
        <button className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}>Overview</button>
        <button className={`tab ${activeTab === 'posts' ? 'active' : ''}`}
          onClick={() => setActiveTab('posts')}>Posts</button>
        <button className={`tab ${activeTab === 'created' ? 'active' : ''}`}
          onClick={() => setActiveTab('created')}>Created</button>
        {profile.isOwnProfile && (
          <button className={`tab ${activeTab === 'saved' ? 'active' : ''}`}
            onClick={() => setActiveTab('saved')}>Saved</button>
        )}
        <button className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}>Statistics</button>
        <button className={`tab ${activeTab === 'achievements' ? 'active' : ''}`}
          onClick={() => setActiveTab('achievements')}>Achievements</button>
      </div>

      <div className="profile-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            <div className="learning-focus">
              <h3>Learning Focus</h3>
              <div className="focus-topics">
                {profile.profile?.preferredTopics?.length > 0 ? (
                  profile.profile.preferredTopics.map(topic => (
                    <span key={topic} className="topic-tag">{topic}</span>
                  ))
                ) : (
                  <p className="no-topics">No preferred topics set</p>
                )}
              </div>
            </div>
            {profile.metrics?.streaks?.current > 0 && (
              <div className="streak-section">
                <h3>Current Streak</h3>
                <div className="streak-info">
                  <span className="streak-number">{profile.metrics.streaks.current}</span>
                  <span className="streak-text">days</span>
                </div>
              </div>
            )}
            <div className="recent-activity">
              <h3>Recent Activity</h3>
              <div className="activity-summary">
                <p>Questions Answered: {profile.metrics?.questionsAnswered || 0}</p>
                <p>Correct Answers: {profile.metrics?.correctAnswers || 0}</p>
                <p>Communities Joined: {profile.stats?.communitiesCount || 0}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'posts' && (
          <div className="posts-section">
            {posts.length > 0 ? (
              posts.map(post => (
                <div key={post._id} className="post-item">
                  <div className="post-content">{post.content}</div>
                  <div className="post-meta">
                    <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                    <span>{post.likes?.length || 0} likes</span>
                    <span>{post.comments?.length || 0} comments</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="no-posts">No posts yet</p>
            )}
          </div>
        )}

        {activeTab === 'created' && (
          <div className="created-section">
            {createdLoading ? (
              <div className="tab-loading"><div className="spinner"></div></div>
            ) : createdContent.length > 0 ? (
              <div className="created-grid">
                {createdContent.map((item, idx) => (
                  <div key={item._id || idx} className="created-item"
                    onClick={() => {
                      if (item.type === 'question' || item.question) {
                        navigate(`/questions/${item.data?._id || item._id}`);
                      } else {
                        navigate(`/examples/${item.data?._id || item._id}`);
                      }
                    }}>
                    <div className="created-item-type">
                      {item.type === 'question' || item.question ? 'Question' : 'Worked Example'}
                    </div>
                    <h4 className="created-item-title">
                      {item.data?.title || item.data?.question?.text || item.data?.content || item.title || 'Untitled'}
                    </h4>
                    <div className="created-item-meta">
                      <span className="grade-badge-sm">Grade {item.data?.grade || item.grade}</span>
                      <span className="topic-badge-sm">{item.data?.topic || item.topic}</span>
                      {(item.data?.difficulty || item.difficulty) && <span className="difficulty-badge-sm">{item.data?.difficulty || item.difficulty}</span>}
                    </div>
                    <div className="created-item-date">
                      {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-content">No created content yet</p>
            )}
          </div>
        )}

        {activeTab === 'saved' && profile.isOwnProfile && (
          <div className="saved-section">
            {savedLoading ? (
              <div className="tab-loading"><div className="spinner"></div></div>
            ) : savedContent.length > 0 ? (
              <div className="saved-grid">
                {savedContent.map((item, idx) => (
                  <div key={item.contentId || idx} className="saved-item"
                    onClick={() => {
                      if (item.contentType === 'question') navigate(`/questions/${item.contentId}`);
                      else if (item.contentType === 'example') navigate(`/examples/${item.contentId}`);
                    }}>
                    <div className="saved-item-type">{item.contentType || 'content'}</div>
                    <h4 className="saved-item-title">
                      {item.title || item.contentType || 'Saved item'}
                    </h4>
                    <div className="saved-item-date">
                      Saved {item.savedAt ? new Date(item.savedAt).toLocaleDateString() : ''}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-content">No saved content yet. Save questions and worked examples from your feed!</p>
            )}
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="stats-section">
            <div className="performance-overview">
              <h3>Performance Overview</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <h4>Total Questions</h4>
                  <p className="stat-number">{stats?.overallStats?.totalQuestions || 0}</p>
                </div>
                <div className="stat-card">
                  <h4>Correct Answers</h4>
                  <p className="stat-number">{stats?.overallStats?.correctAnswers || 0}</p>
                </div>
                <div className="stat-card">
                  <h4>Accuracy</h4>
                  <p className="stat-number">{stats?.overallStats?.accuracy || 0}%</p>
                </div>
                <div className="stat-card">
                  <h4>Longest Streak</h4>
                  <p className="stat-number">{stats?.overallStats?.longestStreak || 0} days</p>
                </div>
              </div>
            </div>
            {stats?.topicsProgress && stats.topicsProgress.length > 0 && (
              <div className="topics-progress">
                <h3>Topic Mastery</h3>
                {stats.topicsProgress.map((topic, index) => (
                  <div key={index} className="topic-progress-item">
                    <div className="topic-header">
                      <span className="topic-name">{topic.topic}</span>
                      <span className="topic-accuracy">{topic.accuracy}%</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${topic.mastery}%` }} />
                    </div>
                    <div className="topic-stats">
                      <span>{topic.questionsAnswered} questions</span>
                      <span>{topic.correctAnswers} correct</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'achievements' && (
          <div className="achievements-section">
            {achievements.length > 0 ? (
              <div className="achievements-grid">
                {achievements.map((achievement, index) => (
                  <div key={index}
                    className={`achievement-card ${achievement.unlocked ? 'unlocked' : 'locked'}`}>
                    <div className="achievement-icon">{achievement.icon}</div>
                    <h4>{achievement.title}</h4>
                    <p>{achievement.description}</p>
                    <div className="achievement-progress">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${achievement.progress}%` }} />
                      </div>
                      <span className="progress-text">
                        {achievement.unlocked ? 'Unlocked!' : `${Math.round(achievement.progress)}%`}
                      </span>
                    </div>
                    {achievement.unlockedAt && (
                      <p className="unlock-date">
                        Unlocked on {new Date(achievement.unlockedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-achievements">No achievements yet</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
